import {Button, DataStatus, RestrictedStatus, showNotification} from 'platform/components';
import {Box, Center, HStack, Show, Space, Spinner, VStack} from 'platform/foundation';
import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';
// eslint-disable-next-line no-restricted-imports -- Intended
import {matchPath, useLocation, useMatch, generatePath} from 'react-router-dom';

import {always, find, isNil, map, propEq} from 'ramda';
import {isArray, isNotString} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {RowControlWrapper} from 'features/datagrid';

import {useNavigationBreadCrumb} from '../../hooks/useNavigationBreadCrumb';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {NumberedMenu} from '../NumberedMenu/NumberedMenu';
import {NumberedMenuItem} from '../NumberedMenu/NumberedMenuItem';

export type RoutedContent = {route: string; element: ReactNode};

export type NavigationItem = {
  content?: ReactNode | RoutedContent[];
  href: string;
} & NumberedMenuItem;

export interface PageProps extends RequiredTestIdProps {
  navigation?: NavigationItem[];
  header?: ReactNode; //TODO: Use new EntityHeaderProps when ready / https://carvago.atlassian.net/browse/T20-15024
  children?: ReactNode;
  activityPanel?: ReactNode;
  isFullHeight?: boolean;
  onNavigate?: (item: NavigationItem) => void;
  isLocked?: boolean;
  isLoading?: boolean;
}

export function Page(props: PageProps) {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const [breadcrumbProps] = useNavigationBreadCrumb();
  const rowControlDetailMatch = useMatch(breadcrumbProps?.rowControlDetail ?? '');

  const [canReadSalesActivities] = usePermissions({
    permissionKeys: ['readSalesActivities'],
  });

  const selectedItem = props.navigation?.find(({href, content}) => {
    const routes = [href.split('?')[0]];
    if (isArray(content)) {
      routes.push(...map((item) => item.route, content));
    }
    return routes.some((route) => matchPath(route, pathname));
  });

  const activeItem = selectedItem || props.navigation?.[0];
  const itemElement = isArray(activeItem?.content)
    ? activeItem?.content.find((routedContent) => matchPath(routedContent.route, pathname))?.element
    : activeItem?.content;

  const height = props.isFullHeight ? '100%' : undefined;
  // When activityPanel is present, the HStack must be height:'auto' so its content can overflow
  // #dms-app-container and create real scroll travel — required for position:sticky on the sidebar.
  // This applies regardless of the REDESIGN flag; the flag only affects the sidebar component itself.
  const autoHeight = props.isFullHeight && props.activityPanel ? 'auto' : height;

  const onItemChange = (itemId: string) => {
    const selectedItem = find(propEq(itemId, 'id'), props.navigation ?? []);
    if (isNil(selectedItem)) {
      return;
    }
    navigate(selectedItem.href);
    props.onNavigate?.(selectedItem);
  };

  const redirectToUrl = () => {
    const redirectToOverviewUrl = props.navigation?.[0].href;

    if (!redirectToOverviewUrl) {
      showNotification.error(i18n.t('page.detailRestrictedArea.noRedirectToOverviewUrl'));
      return;
    }

    navigate(redirectToOverviewUrl);
  };

  const handleRowControlChange = (newEntity: string, sourceSearchParams: string) => {
    if (isNotString(breadcrumbProps?.rowControlDetail)) {
      throw Error('You need to specify rowControlDetail');
    }

    navigate(
      generatePath(breadcrumbProps?.rowControlDetail as string, {
        ...rowControlDetailMatch?.params,
        id: newEntity,
      }) + `?${sourceSearchParams}`
    );
  };

  const disabledOverlay = (
    <Center width="100%">
      <VStack>
        <RestrictedStatus
          headline={i18n.t('page.detailRestrictedArea.headline')}
          subheadline={i18n.t('page.detailRestrictedArea.subheadline')}
        />
        <Space vertical={4} />
        <Button
          data-testid={suffixTestId('goToOverview', props)}
          title={i18n.t('page.detailRestrictedArea.goToOverview')}
          onClick={redirectToUrl}
        />
        <Space vertical={4} />
        <Center>
          <RowControlWrapper onChange={handleRowControlChange} />
        </Center>
      </VStack>
    </Center>
  );

  const loadingOverlay = (
    <Center width="100%">
      <DataStatus isLoading />
    </Center>
  );

  const isActiveItemDisabled = activeItem && 'isDisabled' in activeItem && activeItem?.isDisabled;

  const context = match([isActiveItemDisabled, props.isLoading])
    .with([true, true], always(loadingOverlay))
    .with([true, false], always(disabledOverlay))
    .otherwise(always(props.children ?? itemElement));

  return (
    <VStack height={height}>
      {props.header}
      <HStack height={autoHeight}>
        <Show when={props.navigation}>
          <Box width={54} minWidth={54}>
            <Show when={props.navigation}>
              <Box paddingTop={4} paddingLeft={4} paddingBottom={4}>
                <NumberedMenu
                  items={props.navigation ?? []}
                  activeItemId={activeItem?.id}
                  onItemChange={onItemChange}
                  data-testid={suffixTestId('page-navigation', props)}
                />
              </Box>
            </Show>
          </Box>
        </Show>
        {props.isLocked ? (
          <div
            css={css`
              position: relative;
              height: 100%;
              width: 100%;
              display: flex;
              overflow: hidden;
            `}
          >
            {context}
            <div
              css={css`
                cursor: not-allowed;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                height: 100%;
                background-color: rgba(0, 0, 0, 0.1);
                z-index: 99999;
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <Spinner />
            </div>
          </div>
        ) : (
          <>
            {context}
            <Show
              when={props.activityPanel && canReadSalesActivities}
              whenFeatureEnabled={featureFlags.CRM_SALES_ACTIVITIES}
            >
              {props.activityPanel}
            </Show>
          </>
        )}
      </HStack>
    </VStack>
  );
}
